// locale-unextracted-ignore-file
import { apiClient } from '@app/api';
import { Page } from '@app/components/page/Page';
import { Client, Paginated, User } from '@mi-front/core';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { AutocompleteChangeReason, Box } from '@mui/material';
import { SyntheticEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orderBy } from 'lodash';
import { AutocompleteElement, FormContainer } from 'react-hook-form-mui';
import { ImpersonationUserListItem } from '../components/ImpersonationUserListItem';
import { RecentUsersQuickLogin } from '../components/RecentUsersQuickLogin';
import { useImpersonation } from '../hooks/useImpersonationStore';
import {
  getImpersonationGroupOrder,
  getImpersonationUserGroupKey,
  ImpersonationUserGroupKey,
} from '../impersonation-user-groups';

const IMPERSONATION_GROUP_LABELS: Record<ImpersonationUserGroupKey, string> = {
  mediainbox: 'Usuarios Mediainbox',
  client_admin: 'Administradores de cliente',
  client_user: 'Usuarios de cliente',
};

type FormData = {
  user: (User & { client: Client }) | null;
};

export function ImpersonationPage() {
  const { impersonatedUser, setImpersonatedUser } = useImpersonation();
  const title = 'Admin impersonation';

  const usersQuery = useQuery<Paginated<User & { client: Client }>>({
    queryKey: ['admin-all-users'],
    queryFn: () =>
      apiClient.get('/admin/users', {
        params: {
          contain: 'client',
          pageSize: 2000,
        },
      }),
  });

  const usersWithClient = usersQuery.data?.results.filter((u) => u.client) ?? [];
  const options = orderBy(
    usersWithClient,
    [(u) => getImpersonationGroupOrder(getImpersonationUserGroupKey(u)), 'client.name', 'email'],
    ['asc', 'asc', 'asc'],
  );

  /** Starts impersonation and sends the user to the app root (same as recent-user quick login). */
  function startImpersonation(user: User & { client: Client }) {
    setImpersonatedUser(user);
    setTimeout(() => {
      window.location.href = '/';
    }, 0);
  }

  function handleAutocompleteUserChange(
    _event: SyntheticEvent,
    value: FormData['user'],
    reason: AutocompleteChangeReason,
  ) {
    if (reason !== 'selectOption' || !value) {
      return;
    }
    startImpersonation(value);
  }

  return (
    <Page htmlTitle={title}>
      <Page.Header icon={<AdminPanelSettingsIcon />} title={title} />
      <Page.Content maxWidth="md">
        <FormContainer
          FormProps={{
            onSubmit: (event) => event.preventDefault(),
          }}
          values={{
            user: impersonatedUser,
          }}
        >
          <Box>
            <AutocompleteElement
              name="user"
              options={options}
              label="User"
              loading={usersQuery.isLoading}
              textFieldProps={{
                margin: 'normal',
                fullWidth: true,
              }}
              autocompleteProps={{
                disabled: usersQuery.isLoading,
                slotProps: {
                  listbox: {
                    sx: { maxHeight: 'calc(100vh - 300px)' },
                  },
                },
                groupBy: (option) =>
                  IMPERSONATION_GROUP_LABELS[getImpersonationUserGroupKey(option)],
                getOptionLabel: (option) =>
                  option ? `${option.client?.name} - ${option.email}` : '',
                isOptionEqualToValue: (option, value) => option?.id === value?.id,
                renderOption: (props, option) => (
                  <ImpersonationUserListItem {...props} user={option} />
                ),
                filterOptions: (options, { inputValue }) => {
                  return options.filter((option) =>
                    `${option.client?.name || ''} ${option.email} ${option.name} ${option.surname} ${
                      option.role
                    }`
                      .trim()
                      .toLowerCase()
                      .includes(inputValue.trim().toLowerCase()),
                  );
                },
                onChange: handleAutocompleteUserChange,
              }}
            />
          </Box>
        </FormContainer>

        <RecentUsersQuickLogin onUserSelect={startImpersonation} />
      </Page.Content>
    </Page>
  );
}
